import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { api } from './api';

/**
 * The unread count, shared by the header badge and the inbox.
 *
 * This exists because the badge and the screen that changes it are siblings.
 * The shell fetched its own count once on mount, so reading a notification
 * could not reach it — the badge stayed put until the shell remounted, which
 * for a single-page app means until the worker closed the app.
 *
 * A count from the server, not a measured list. The previous version fetched
 * twenty rows and took `.length`, which is a wasted payload on a 3G handset
 * and quietly wrong from twenty-one onwards.
 *
 * Deliberately small. This is the app's first genuine cross-screen
 * invalidation, and one shared number does not justify a cache library; the
 * moment there is a second, `useQuery` should become TanStack Query and this
 * file should disappear into it.
 */

interface UnreadState {
  count: number;
  /** Re-reads the count. Call after anything that changes read state. */
  refresh(): void;
  /** Moves the badge immediately, before the server round trip. */
  setCount(n: number): void;
}

const Ctx = createContext<UnreadState | null>(null);

export function UnreadProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    api.get<{ unread: number }>('/me/notifications/unread-count')
      .then((r) => { if (live) setCount(r.unread); })
      // A badge that cannot be fetched shows nothing. It is an ornament on
      // every screen but one, and an error banner about it would be noise.
      .catch(() => undefined);
    return () => { live = false; };
  }, [nonce]);

  const value = useMemo<UnreadState>(
    () => ({ count, refresh, setCount }), [count, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUnread(): UnreadState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUnread must be used inside UnreadProvider');
  return ctx;
}
